import { Fragment, useRef, useState } from 'react';
import { View } from 'react-native';
import ActionSheet from 'react-native-actions-sheet';
import { Text } from '@ui-kitten/components';
import lodashIsEmpty from 'lodash/isEmpty';

import ThemeStyle from '../../Theme/styles';
import { SECTION_TITLE } from '../../Theme/typographyProps';

import Button from '../Button';
import HighlightText from '../HighlightText';

import commonProps from './commonProps';
import styles from './styles';

/**
 * useful data shape is
 * {
 *   title: string,
 *   message: string,
 *   highlight: [{ word: string, style: HTMLStyle }],
 *   buttonStatus: string,
 *   buttonText: string,
 *   buttons: [{ text: string, onPress: Function or Promise, status: "ui kitten button status" }]
 * }
 */
function PromptSheet({ sheetId }) {
  const sheetRef = useRef();
  const [data, setData] = useState();

  const title = data?.title || '';
  const message = data?.message || '';
  const highlight = data?.highlight || [];
  const buttonStatus = data?.buttonStatus || '';
  const buttonText = data?.buttonText || '';
  const buttons = data?.buttons || [];

  const hasButton =
    (Boolean(buttonStatus) && Boolean(buttonText)) || !lodashIsEmpty(buttons);

  // by using sheetRef.current.hide() instead of modals.hide() this fixed the issue of cannot hide after show inside of other action sheet
  const _onClose = () => sheetRef.current.hide({ buttonClicked: true });

  const _onButtonPress = (data) => async () => {
    data?.onPress?.();
    return sheetRef.current.hide(data);
  };

  const _renderButton = () => {
    if (Boolean(buttonStatus) && Boolean(buttonText)) {
      return (
        <Button status={buttonStatus} onPress={_onClose}>
          {() => <Text appearance="alternative">{buttonText}</Text>}
        </Button>
      );
    } else if (!lodashIsEmpty(buttons)) {
      const isRow = buttons.length <= 2;
      return (
        <View
          style={isRow && [ThemeStyle.flexWrap, ThemeStyle.flexDirectionRow]}
        >
          {buttons.map((button, i) => (
            <Fragment key={i}>
              <Button
                status={button.status || 'success'}
                style={isRow && ThemeStyle.flex1}
                onPress={_onButtonPress(button)}
              >
                {(props) => (
                  <Text {...props} numberOfLines={isRow ? 1 : undefined}>
                    {button.text}
                  </Text>
                )}
              </Button>
              {/* If not last, add separator */}
              {i !== buttons.length - 1 && (
                <View
                  style={
                    isRow
                      ? ThemeStyle.rowSeparatorSmall
                      : ThemeStyle.spacingBottomSmall
                  }
                />
              )}
            </Fragment>
          ))}
        </View>
      );
    }
    return null;
  };

  return (
    <ActionSheet
      id={sheetId}
      ref={sheetRef}
      onBeforeShow={setData}
      {...commonProps}
    >
      <View style={styles.contentContainer}>
        <Text style={ThemeStyle.spacingBottomMedium} {...SECTION_TITLE}>
          {title}
        </Text>

        <View style={hasButton && ThemeStyle.spacingBottom}>
          <HighlightText category="p2" highlight={highlight} text={message} />
        </View>

        {_renderButton()}
      </View>
    </ActionSheet>
  );
}

export default PromptSheet;
